<template>
  <Modal
    :title="modalTitle"
    :width="650"
    name="CREATE_UPDATE_OWN_BRANCH_MODAL"
    ref="createUpdateOwnBranchModal"
    actions
  >
    <template v-slot:body>
      <h-loading v-if="isLoading" />

      <v-form ref="branchForm" v-model="valid">
        <!-- Read-only: the backend refuses an address that resolves to another country, because the
             branch country drives BR/AU appointment behaviour for every appointment on it. -->
        <HCountrySelect
          v-model="country"
          :country="country"
          :disabled="true"
        />

        <v-text-field
          v-model="name"
          :label="$t('customer.branches.label.name')"
          :rules="[rules.required]"
          :disabled="isSavingBranch"
          outlined
        />

        <HDocumentID
          v-model="cnpj"
          :rules="[rules.documentCheck]"
          :default-value="cnpj"
          :country="country"
        />

        <AddressSearch
          @address="getAddressData"
          :rules="[rules.required]"
          :disabled="isSavingBranch"
          :selectedCountry="country"
          :getFocus="false"
          ref="addressSearch"
        />

        <v-text-field
          v-model="email"
          :label="$t('customer.branches.label.email')"
          :rules="[rules.email]"
          :disabled="isSavingBranch"
          outlined
        />

        <HPhone
          v-model="phone"
          :default-value="phone"
          :country="country"
        />

        <v-row>
          <v-col class="pb-0" cols="12">
            <v-text-field
              v-model="resumeContact"
              :label="$t('customer.branches.label.resumeContact')"
              :rules="[rules.emailCheckPassEmpty]"
              :disabled="isSavingBranch"
              outlined
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeModal('CREATE_UPDATE_OWN_BRANCH_MODAL')"
        elevation="0"
        color="grey"
        text
      >
        {{ $t('realEstate.actions.cancel') }}
      </v-btn>
      <v-btn
        @click="save"
        :loading="isSavingBranch"
        :disabled="!valid"
        color="primary"
        elevation="0"
        class="ml-5"
      >
        {{ $t('realEstate.actions.save') }}
      </v-btn>
    </template>
  </Modal>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { email, phone, required, documentCheck, emailCheckPassEmpty } from '@/util/errorHandling'
import { unmaskText } from '@/util/helpers'

import Modal from '@/components/app/Modal'
import AddressSearch from '@/components/app/AddressSearch'
import HDocumentID from '@/components/app/HDocumentID'
import HPhone from '@/components/app/HPhone'
import HCountrySelect from '@/components/app/HCountrySelect'

export default {
  name: 'CreateUpdateBranch',

  components: {
    Modal,
    AddressSearch,
    HDocumentID,
    HPhone,
    HCountrySelect,
  },

  computed: {
    ...mapGetters(['ownBranch', 'isLoading', 'isSavingBranch']),

    modalTitle() {
      return this.branchId
        ? this.$t('customer.branches.modals.form.updateTitle')
        : this.$t('customer.branches.modals.form.createTitle')
    },
  },

  methods: {
    ...mapActions(['saveOwnBranch']),
    ...mapMutations(['closeModal']),

    getAddressData(data) {
      this.address = data.address
    },

    fillFields() {
      if (!this.ownBranch.id) return

      this.$nextTick(() => {
        this.branchId = this.ownBranch.id
        this.cnpj = this.ownBranch.cnpj
        this.name = this.ownBranch.name
        this.email = this.ownBranch.email
        this.phone = this.ownBranch.phone
        this.address = this.ownBranch.address
        this.resumeContact = this.ownBranch.appointment_resume_contact
        this.setAddressSearchValue(this.ownBranch.address)
      })
    },

    /**
     * AddressSearch renders `googleAddress.address`. Replacing the whole object keeps it reactive -
     * assigning the key in place does not, since Vue 2 cannot track a key added to a plain object.
     */
    setAddressSearchValue(address) {
      const addressSearch = this.$refs.addressSearch
      if (!addressSearch) return
      addressSearch.googleAddress = { ...addressSearch.googleAddress, address }
    },

    buildBody() {
      return {
        branch_id: this.branchId ? this.branchId : null,
        cnpj: unmaskText(this.cnpj),
        name: this.name,
        email: this.email,
        address: this.address,
        phone: this.phone,
        appointment_resume_contact: this.resumeContact,
      }
    },

    save() {
      this.saveOwnBranch(this.buildBody())
    },

    resetForm() {
      this.$refs.branchForm.resetValidation()

      this.$nextTick(() => {
        this.branchId = null
        this.cnpj = ''
        this.name = ''
        this.email = ''
        this.phone = null
        this.address = ''
        this.resumeContact = ''
        this.country = this.userCountry()
        this.setAddressSearchValue('')
      })

      this.$nextTick(() => this.fillFields())
    },

    userCountry() {
      return localStorage.getItem('user_country') ?? 'BR'
    },
  },

  data() {
    return {
      valid: true,
      branchId: null,
      cnpj: '',
      name: '',
      address: '',
      email: '',
      phone: null,
      resumeContact: '',
      country: localStorage.getItem('user_country') ?? 'BR',
      rules: {
        email,
        phone,
        required,
        documentCheck,
        emailCheckPassEmpty,
      },
    }
  },

  mounted() {
    this.$watch('$refs.createUpdateOwnBranchModal.dialog', (isOpen) => {
      if (isOpen) this.resetForm()
    })
  },
}
</script>
